<script setup lang="ts">
/**
 * `/partner` — Partner / referral page (T06).
 *
 * Sections:
 *   1. Header — title + tagline.
 *   2. Hero — large referral code (copy-to-clipboard) + shareable URL
 *      (`https://opengonka.com/?ref=<code>`).
 *   3. KPI row — Total referrals / Active referrals / Total earned (USD).
 *   4. Referrals table — created_at / referee shortId / first_charge_at /
 *      earned_usd, paginated (page/page_size from the store).
 *   5. Empty state — friendly nudge when `referrals=[]` on the first page.
 *
 * Data comes from `usePartnerStore` (T11 backend). All money values arrive
 * as decimal strings and are formatted with `formatUsd` via Number(...) at
 * the very last step to avoid float drift on the wire.
 *
 * Copy-to-clipboard uses `navigator.clipboard` in secure contexts and falls
 * back to a transient hidden `<textarea>` for http://localhost development.
 */
import { computed, onMounted, ref } from 'vue';
import { usePartnerStore } from '../stores/partner';
import { useAuthStore } from '../stores/auth';
import { formatDateTime, formatUsd } from '../lib/format';

const partner = usePartnerStore();
const auth = useAuthStore();

/**
 * Share-URL host resolution.
 *
 * The hub publishes its canonical public origin via `GET /api/config`
 * (`PublicConfig.hubOrigin`) which `useAuthStore` already loads at boot.
 * That's the single source of truth — using it keeps copied referral links
 * correct on staging, alternate domains, and dev (e.g. http://localhost:5173).
 *
 * Fallback chain when config hasn't loaded yet:
 *   1. `auth.config?.hubOrigin` — server-provided canonical host
 *   2. `window.location.origin` — current page origin (best-effort)
 *   3. literal `https://opengonka.com` — last-resort prod default so the
 *      copied link is never empty/malformed during the brief bootstrap window.
 */
const shareHost = computed<string>(() => {
  const fromConfig = auth.config?.hubOrigin;
  if (fromConfig && fromConfig.length > 0) return fromConfig.replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://opengonka.com';
});

const shareUrl = computed<string>(() => {
  const code = partner.summary?.referral_code;
  return code ? `${shareHost.value}/?ref=${encodeURIComponent(code)}` : '';
});

const totalEarnedUsd = computed<string>(() => {
  const raw = partner.summary?.total_earned_usd;
  if (raw == null) return '—';
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return formatUsd(n);
});

/**
 * Empty state should only show after we've successfully loaded the first
 * page — not while we're still loading nor when the request errored.
 */
const showEmptyState = computed<boolean>(
  () =>
    !partner.loadingList &&
    partner.error == null &&
    partner.referrals.length === 0 &&
    partner.page === 1,
);

const copiedCode = ref<boolean>(false);
const copiedLink = ref<boolean>(false);
let codeTimer: ReturnType<typeof setTimeout> | null = null;
let linkTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Copy `text` to the system clipboard.
 *
 * Prefers the async Clipboard API in secure contexts (HTTPS and localhost
 * both qualify as `isSecureContext === true`). Falls back to a transient
 * hidden `<textarea>` + `document.execCommand('copy')` for genuinely
 * non-secure origins (e.g. http://192.168.x.x dev rigs) where the
 * Clipboard API is unavailable or rejected.
 */
async function copyText(text: string): Promise<boolean> {
  if (!text) return false;
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the textarea path
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } finally {
      document.body.removeChild(ta);
    }
    return ok;
  } catch {
    return false;
  }
}

async function copyCode(): Promise<void> {
  const code = partner.summary?.referral_code;
  if (!code) return;
  const ok = await copyText(code);
  if (!ok) return;
  copiedCode.value = true;
  if (codeTimer) clearTimeout(codeTimer);
  codeTimer = setTimeout(() => {
    copiedCode.value = false;
  }, 1500);
}

async function copyLink(): Promise<void> {
  if (!shareUrl.value) return;
  const ok = await copyText(shareUrl.value);
  if (!ok) return;
  copiedLink.value = true;
  if (linkTimer) clearTimeout(linkTimer);
  linkTimer = setTimeout(() => {
    copiedLink.value = false;
  }, 1500);
}

function formatEarnedUsd(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return formatUsd(n);
}

onMounted(() => {
  void partner.refresh();
});
</script>

<template>
  <div class="p-6 flex flex-col gap-6 max-w-6xl mx-auto w-full">
    <!-- Header -->
    <header>
      <h1 class="text-2xl font-semibold">Partner</h1>
      <p class="text-muted text-sm mt-2">
        Refer users and earn a share of their spend.
      </p>
    </header>

    <!-- Top-level error (covers either summary or referrals failure) -->
    <div
      v-if="partner.error"
      class="card p-4 border border-danger text-danger text-sm flex items-center justify-between gap-3"
    >
      <span>{{ partner.error }}</span>
      <button class="btn-ghost text-xs" type="button" @click="partner.refresh()">Retry</button>
    </div>

    <!-- Hero — referral code + share link -->
    <section class="card p-6 flex flex-col gap-4">
      <div>
        <h2 class="text-sm font-medium text-muted uppercase tracking-wide">Your referral code</h2>

        <!-- While loading the summary, keep a skeleton-ish placeholder so the
             layout doesn't jump. -->
        <div
          v-if="partner.loadingSummary && !partner.summary"
          class="mt-3 text-2xl font-mono text-muted"
        >
          loading…
        </div>

        <div v-else-if="partner.summary" class="mt-3 flex flex-wrap items-center gap-3">
          <code
            class="text-3xl font-mono font-semibold text-ink bg-code border border-border rounded-md px-4 py-2"
          >
            {{ partner.summary.referral_code }}
          </code>
          <button
            type="button"
            class="btn-ghost"
            :aria-label="copiedCode ? 'Copied to clipboard' : 'Copy referral code'"
            @click="copyCode"
          >
            {{ copiedCode ? 'Copied' : 'Copy code' }}
          </button>
        </div>
      </div>

      <div v-if="partner.summary">
        <h3 class="text-sm font-medium text-muted uppercase tracking-wide">Share link</h3>
        <div
          class="mt-2 flex flex-wrap items-center gap-3 bg-code border border-border rounded-md p-3 font-mono text-xs"
        >
          <span class="break-all flex-1 min-w-0 text-ink">{{ shareUrl }}</span>
          <button
            type="button"
            class="btn-ghost text-xs shrink-0"
            :aria-label="copiedLink ? 'Copied to clipboard' : 'Copy share link'"
            @click="copyLink"
          >
            {{ copiedLink ? 'Copied' : 'Copy link' }}
          </button>
        </div>
      </div>
    </section>

    <!-- KPI cards -->
    <section class="grid gap-4 sm:grid-cols-3" aria-label="Referral statistics">
      <article class="card p-4">
        <div class="text-xs text-muted uppercase tracking-wide">Total referrals</div>
        <div class="text-2xl font-semibold mt-2">
          {{ partner.summary?.total_referrals ?? '—' }}
        </div>
      </article>
      <article class="card p-4">
        <div class="text-xs text-muted uppercase tracking-wide">Active referrals</div>
        <div class="text-2xl font-semibold mt-2">
          {{ partner.summary?.active_referrals ?? '—' }}
        </div>
        <p class="text-[11px] text-muted mt-1">
          Referees with at least one ledger entry.
        </p>
      </article>
      <article class="card p-4">
        <div class="text-xs text-muted uppercase tracking-wide">Total earned</div>
        <div class="text-2xl font-semibold text-ok mt-2">{{ totalEarnedUsd }}</div>
        <p class="text-[11px] text-muted mt-1">Lifetime, USD.</p>
      </article>
    </section>

    <!-- Referrals table -->
    <section class="card p-6">
      <div class="flex items-baseline justify-between gap-3 mb-4">
        <h2 class="text-lg font-semibold">Referrals</h2>
        <span v-if="partner.total > 0" class="text-xs text-muted">
          Page {{ partner.page }} of {{ partner.totalPages }} · {{ partner.total }} total
        </span>
      </div>

      <div v-if="partner.loadingList && partner.referrals.length === 0" class="text-sm text-muted">
        Loading referrals…
      </div>

      <!-- Empty state — only after a clean load of page 1 with no rows. -->
      <div
        v-else-if="showEmptyState"
        class="border border-dashed border-border rounded-lg p-8 text-center text-muted text-sm"
      >
        Share your link to start earning.
      </div>

      <div v-else-if="partner.referrals.length > 0" class="overflow-x-auto">
        <table class="table w-full min-w-[640px]">
          <thead>
            <tr>
              <th class="w-48">Created</th>
              <th>Referee</th>
              <th class="w-48">First charge</th>
              <th class="w-32 text-right">Earned</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in partner.referrals" :key="row.referee_user_id_short + row.referred_at">
              <td class="text-sm">{{ formatDateTime(row.referred_at) }}</td>
              <td>
                <code class="text-xs">{{ row.referee_user_id_short }}</code>
              </td>
              <td class="text-sm text-muted">
                {{ row.first_charge_at ? formatDateTime(row.first_charge_at) : '—' }}
              </td>
              <td class="text-sm text-ok text-right">{{ formatEarnedUsd(row.earned_usd) }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div
          v-if="partner.totalPages > 1"
          class="mt-4 flex items-center justify-end gap-2"
        >
          <button
            type="button"
            class="btn-ghost text-xs"
            :disabled="partner.page <= 1 || partner.loadingList"
            @click="partner.prevPage()"
          >
            Previous
          </button>
          <button
            type="button"
            class="btn-ghost text-xs"
            :disabled="partner.page >= partner.totalPages || partner.loadingList"
            @click="partner.nextPage()"
          >
            Next
          </button>
        </div>
      </div>
    </section>
  </div>
</template>
